import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import "./signup.css";
import { apiFetch, readJsonSafe } from "../config/apiClient";
import {
  getUser,
  isAuthenticated,
} from "../config/auth";

const OTP_LENGTH = 6;
const OTP_COOLDOWN_SECONDS = 60;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;

const mapDuplicateConflictMessage = (message) => {
  const text = String(message || "").trim();
  if (/email already exists|already registered/i.test(text)) {
    return "This email is already registered. Please log in instead.";
  }
  if (/username already exists/i.test(text)) {
    return "Username already exists";
  }
  return text;
};

const parseOtpError = (message, fallback) => {
  const text = String(message || "").trim();
  if (!text) return fallback;
  if (/already exists/i.test(text)) {
    return mapDuplicateConflictMessage(text);
  }
  if (/please wait 60 seconds/i.test(text)) {
    return "Please wait 60 seconds before requesting another OTP";
  }
  if (/request limit exceeded/i.test(text)) {
    return "Too many OTP requests. Please try again later.";
  }
  if (/invalid otp/i.test(text)) return "Invalid OTP";
  if (/otp has expired/i.test(text)) return "OTP has expired";
  if (/email otp is not verified/i.test(text)) return "Email OTP is not verified";
  return text || fallback;
};

function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
  });
  const [otp, setOtp] = useState("");
  const [otpStep, setOtpStep] = useState("email");
  const [isOtpSending, setIsOtpSending] = useState(false);
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [bannerMessage, setBannerMessage] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!isAuthenticated()) return;
    const role = String(getUser()?.role || "STUDENT").toUpperCase();
    navigate(role === "ADMIN" ? "/admin/dashboard" : "/dashboard", { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (cooldownSeconds <= 0) return undefined;
    const timerId = window.setTimeout(() => {
      setCooldownSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => window.clearTimeout(timerId);
  }, [cooldownSeconds]);

  const normalizedEmail = useMemo(
    () => formData.email.trim().toLowerCase(),
    [formData.email]
  );
  const isEmailVerified = otpStep === "details";
  const canVerifyOtp = otp.trim().length === OTP_LENGTH && /^\d{6}$/.test(otp.trim());
  const resendLabel = cooldownSeconds > 0 ? `Resend OTP in ${cooldownSeconds}s` : "Resend OTP";
  const hasDuplicateEmailConflict = /already registered/i.test(error);

  const resetOtpState = () => {
    setOtp("");
    setOtpStep("email");
    setOtpError("");
    setBannerMessage("");
    setCooldownSeconds(0);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "email") {
      const nextEmail = value;
      const previousEmail = normalizedEmail;
      setFormData((prev) => ({ ...prev, [name]: nextEmail }));
      setError("");
      setSuccess("");
      setOtpError("");
      setBannerMessage("");

      if (previousEmail && previousEmail !== nextEmail.trim().toLowerCase() && isEmailVerified) {
        resetOtpState();
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  const validateEmail = () => {
    if (!normalizedEmail) {
      setError("Please enter your email address.");
      return false;
    }
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setError("Please enter a valid email address.");
      return false;
    }
    return true;
  };

  const sendOtp = async () => {
    if (!validateEmail()) return;

    setIsOtpSending(true);
    setError("");
    setOtpError("");
    setBannerMessage("");
    setSuccess("");

    try {
      const response = await apiFetch("/users/signup/send-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailId: normalizedEmail }),
      });
      const data = await readJsonSafe(response);

      if (!response.ok) {
        if (response.status === 409) {
          setOtpStep("email");
          setCooldownSeconds(0);
          throw new Error(mapDuplicateConflictMessage(data?.message || "Email already exists"));
        }
        if (response.status === 500) {
          throw new Error("Unable to send OTP right now. Please try again.");
        }
        const mapped = parseOtpError(data?.message, "Unable to send OTP right now.");
        if (/please wait 60 seconds/i.test(mapped)) {
          setCooldownSeconds(OTP_COOLDOWN_SECONDS);
        }
        if (/Too many OTP requests/i.test(mapped)) {
          setCooldownSeconds(OTP_COOLDOWN_SECONDS);
          setBannerMessage(mapped);
          return;
        }
        throw new Error(mapped);
      }

      setOtpStep("otp");
      setCooldownSeconds(OTP_COOLDOWN_SECONDS);
      setSuccess("OTP sent successfully. Please check your email.");
    } catch (err) {
      setError(err.message || "Unable to send OTP right now.");
    } finally {
      setIsOtpSending(false);
    }
  };

  const verifyOtp = async () => {
    if (!validateEmail()) return;
    if (!canVerifyOtp) {
      setOtpError("OTP should be exactly 6 digits.");
      return;
    }

    setIsOtpVerifying(true);
    setOtpError("");
    setError("");
    setSuccess("");

    try {
      const response = await apiFetch("/users/signup/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailId: normalizedEmail, otp: otp.trim() }),
      });
      const data = await readJsonSafe(response);

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(mapDuplicateConflictMessage(data?.message || "Email already exists"));
        }
        const mapped = parseOtpError(data?.message, "OTP verification failed.");
        if (/Email OTP is not verified/i.test(mapped)) {
          setOtpStep("otp");
        }
        throw new Error(mapped);
      }

      setOtpStep("details");
      setOtpError("");
      setSuccess("Email verified. Complete your signup details.");
    } catch (err) {
      const message = err.message || "OTP verification failed.";
      if (/Invalid OTP|OTP has expired/i.test(message)) {
        setOtpError(message);
      } else {
        setError(message);
      }
    } finally {
      setIsOtpVerifying(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const name = formData.name.trim();
    const phone = formData.phone.trim();
    const password = formData.password;
    const confirmPassword = formData.confirmPassword;

    if (!isEmailVerified) {
      setError("Please verify your email with OTP before creating the account.");
      return;
    }

    if (!name || !normalizedEmail || !phone || !password || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }

    if (!PHONE_REGEX.test(phone)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    if (!hasUpper || !hasLower || !hasDigit) {
      setError("Password must include uppercase, lowercase and a number.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        username: name,
        emailId: normalizedEmail,
        phone,
        password,
      };

      const response = await apiFetch("/users/signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await readJsonSafe(response);

      if (!response.ok) {
        if (response.status === 409) {
          throw new Error(mapDuplicateConflictMessage(data?.message || "Signup failed."));
        }
        throw new Error(parseOtpError(data?.message, "Signup failed."));
      }

      setSuccess("Account created successfully! Please log in.");
      window.setTimeout(
        () =>
          navigate("/login", {
            replace: true,
            state: {
              loginMessage: "Account created successfully. Please log in.",
            },
          }),
        1200
      );
    } catch (err) {
      const message = err.message || "Signup failed.";
      if (/Email OTP is not verified/i.test(message)) {
        setOtpStep("otp");
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-container">
      <Helmet>
        <title>Sign Up | AAMSchool</title>
        <link rel="canonical" href="https://www.aamschool.in/signup" />
        <meta
          name="description"
          content="Create your AAMSchool account to access quizzes, exam preparation tools, and saved progress."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="auth-backdrop" />
      <div className="auth-shell">
        <section className="auth-side-panel">
          <Link to="/" className="auth-home-link">
            AAMSCHOOL
          </Link>
          <p className="auth-side-kicker">Create Account</p>
          <h1>Modern OTP onboarding in a single focused modal</h1>
          <p className="auth-side-copy">
            Verify email first, confirm the OTP, and finish registration without leaving the same sleek auth flow.
          </p>
          <div className="auth-side-badges">
            <span>Email verified</span>
            <span>OTP protected</span>
            <span>Fast signup</span>
          </div>
        </section>

        <form className="signup-card auth-modal-card" onSubmit={handleSubmit}>
          <div className="auth-modal-topbar">
            <span className="auth-modal-pill">Sign Up</span>
            <button type="button" className="auth-close-btn" onClick={() => navigate("/")}>
              Close
            </button>
          </div>

          <h2>Create Your Account</h2>
          <p className="signup-subtext">Verify your email with OTP before completing signup</p>

          {bannerMessage ? <p className="error-msg">{bannerMessage}</p> : null}
          {error ? <p className="error-msg">{error}</p> : null}
          {hasDuplicateEmailConflict ? (
            <button
              type="button"
              className="signup-btn signup-btn-secondary"
              onClick={() =>
                navigate("/login", {
                  state: {
                    loginMessage: "This email is already registered. Please log in instead.",
                  },
                })
              }
            >
              Go to Login
            </button>
          ) : null}
          {success ? <p className="success-msg">{success}</p> : null}

          <div className="signup-step-chip-row">
            <span className={`signup-step-chip ${otpStep === "email" ? "active" : ""}`}>1. Email</span>
            <span className={`signup-step-chip ${otpStep === "otp" ? "active" : ""}`}>2. OTP</span>
            <span className={`signup-step-chip ${otpStep === "details" ? "active" : ""}`}>3. Details</span>
          </div>

          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={handleChange}
            autoComplete="email"
            maxLength={120}
            readOnly={isEmailVerified}
            className={isEmailVerified ? "is-verified" : ""}
          />

          <button
            type="button"
            className="signup-btn signup-btn-secondary"
            disabled={isOtpSending || cooldownSeconds > 0 || isEmailVerified}
            onClick={sendOtp}
          >
            {isEmailVerified ? "Email Verified" : isOtpSending ? "Sending OTP..." : cooldownSeconds > 0 ? resendLabel : otpStep === "email" ? "Send OTP" : resendLabel}
          </button>

          {otpStep !== "email" ? (
            <>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d*"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(event) => {
                  const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH);
                  setOtp(digitsOnly);
                  setOtpError("");
                  setError("");
                }}
                maxLength={OTP_LENGTH}
              />
              {otpError ? <p className="signup-helper error">{otpError}</p> : null}
              <button
                type="button"
                className="signup-btn signup-btn-secondary"
                disabled={isOtpVerifying || !canVerifyOtp || isEmailVerified}
                onClick={verifyOtp}
              >
                {isEmailVerified ? "OTP Verified" : isOtpVerifying ? "Verifying..." : "Verify OTP"}
              </button>
            </>
          ) : null}

          {isEmailVerified ? (
            <>
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
                maxLength={80}
              />

              <input
                type="tel"
                name="phone"
                placeholder="Mobile Number"
                value={formData.phone}
                onChange={handleChange}
                autoComplete="tel"
                inputMode="numeric"
                maxLength={10}
              />

              <input
                type="password"
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
              />

              <input
                type="password"
                name="confirmPassword"
                placeholder="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
              />

              <button type="submit" className="signup-btn" disabled={loading}>
                {loading ? "Creating..." : "Create Account"}
              </button>
            </>
          ) : null}

          <p className="login-link">
            Already have an account? <Link to="/login">Login here</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default Signup;
